import React, {
  useCallback,
  useRef,
  useState,
} from "react";

import {
  FiUploadCloud,
  FiFileText,
  FiImage,
  FiX,
  FiZap,
  FiCheckCircle,
  FiAlertCircle,
} from "react-icons/fi";

const ACCEPTED_TYPES = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
]);

const MAX_FILES = 10;
const MAX_FILE_SIZE = 15 * 1024 * 1024;

const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes)) return "";

  if (bytes < 1024) {
    return `${bytes} B`;
  }

  if (bytes < 1024 * 1024) {
    return `${(
      bytes / 1024
    ).toFixed(1)} KB`;
  }

  return `${(
    bytes /
    (1024 * 1024)
  ).toFixed(1)} MB`;
};

const fileKey = (file) =>
  `${file.name}-${file.size}-${file.lastModified}`;

const LoadDocumentUploader = ({
  onAnalyze,
  compact = false,
}) => {
  const inputRef = useRef(null);

  const [files, setFiles] =
    useState([]);

  const [dragging, setDragging] =
    useState(false);

  const [analyzing, setAnalyzing] =
    useState(false);

  const [error, setError] =
    useState("");

  const [result, setResult] =
    useState(null);

  const addFiles = useCallback(
    (incomingFiles) => {
      setError("");

      const incoming =
        Array.from(
          incomingFiles || []
        );

      const invalidType =
        incoming.find(
          (file) =>
            !ACCEPTED_TYPES.has(
              file.type
            )
        );

      if (invalidType) {
        setError(
          `Unsupported file: ${invalidType.name}`
        );

        return;
      }

      const oversized =
        incoming.find(
          (file) =>
            file.size >
            MAX_FILE_SIZE
        );

      if (oversized) {
        setError(
          `${oversized.name} exceeds 15 MB.`
        );

        return;
      }

      setFiles((current) => {
        const map =
          new Map();

        current.forEach(
          (file) => {
            map.set(
              fileKey(file),
              file
            );
          }
        );

        incoming.forEach(
          (file) => {
            map.set(
              fileKey(file),
              file
            );
          }
        );

        const next =
          Array.from(
            map.values()
          );

        if (
          next.length >
          MAX_FILES
        ) {
          setError(
            `Maximum ${MAX_FILES} files per import.`
          );

          return current;
        }

        setResult(null);

        return next;
      });
    },
    []
  );

  const handleDrop = (
    event
  ) => {
    event.preventDefault();

    setDragging(false);

    addFiles(
      event.dataTransfer.files
    );
  };

  const removeFile = (
    target
  ) => {
    setFiles((current) =>
      current.filter(
        (file) =>
          fileKey(file) !==
          fileKey(target)
      )
    );

    setResult(null);
  };

  const handleAnalyze =
    async () => {
      if (
        analyzing ||
        files.length === 0
      ) {
        return;
      }

      setAnalyzing(true);
      setError("");
      setResult(null);

      try {
        const analysis =
          await onAnalyze(files);

        setResult(
          analysis || {
            ok: true,
          }
        );
      } catch (err) {
        setError(
          err?.response?.data
            ?.error ||
            err?.message ||
            "Could not analyse the transport order."
        );
      } finally {
        setAnalyzing(false);
      }
    };

  return (
    <section className={`load-ai-import${compact ? " is-compact" : ""}`}>
      <div className="load-ai-import-header">
        <div className="load-ai-import-heading">
          <div className="load-ai-import-icon">
            <FiZap />
          </div>

          <div>
            <h3>
              Create from transport order
            </h3>

            <p>
              Upload the order and
              LOADEvo will pre-fill
              the load for you.
            </p>
          </div>
        </div>

        <div className="load-ai-import-badge">
          AI assisted
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept=".pdf,.jpg,.jpeg,.png,.webp"
        className="load-ai-import-file-input"
        onChange={(event) => {
          addFiles(
            event.target.files
          );

          event.target.value =
            "";
        }}
      />

      <div
        className={[
          "load-ai-import-dropzone",
          dragging
            ? "is-dragging"
            : "",
          files.length
            ? "has-files"
            : "",
        ]
          .filter(Boolean)
          .join(" ")}
        role="button"
        tabIndex={0}
        onClick={() =>
          inputRef.current?.click()
        }
        onKeyDown={(event) => {
          if (
            event.key ===
              "Enter" ||
            event.key === " "
          ) {
            event.preventDefault();

            inputRef.current?.click();
          }
        }}
        onDragEnter={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();

          if (
            event.currentTarget ===
            event.target
          ) {
            setDragging(false);
          }
        }}
        onDrop={handleDrop}
      >
        <div className="load-ai-import-cloud">
          <FiUploadCloud />
        </div>

        <div className="load-ai-import-dropzone-title">
          Drop transport orders
          here
        </div>

        <div className="load-ai-import-dropzone-subtitle">
          or click to select
          files
        </div>

        <div className="load-ai-import-formats">
          PDF · JPG · PNG · WEBP
          · max. 15 MB
        </div>
      </div>

      {files.length > 0 && (
        <div className="load-ai-import-files">
          {files.map((file) => {
            const isImage =
              file.type.startsWith(
                "image/"
              );

            return (
              <div
                key={fileKey(file)}
                className="load-ai-import-file"
              >
                <div className="load-ai-import-file-icon">
                  {isImage ? (
                    <FiImage />
                  ) : (
                    <FiFileText />
                  )}
                </div>

                <div className="load-ai-import-file-details">
                  <div className="load-ai-import-file-name">
                    {file.name}
                  </div>

                  <div className="load-ai-import-file-size">
                    {formatBytes(
                      file.size
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  className="load-ai-import-remove"
                  aria-label={`Remove ${file.name}`}
                  disabled={
                    analyzing
                  }
                  onClick={(
                    event
                  ) => {
                    event.stopPropagation();

                    removeFile(
                      file
                    );
                  }}
                >
                  <FiX />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {error && (
        <div className="load-ai-import-message load-ai-import-message--error">
          <FiAlertCircle />

          <span>{error}</span>
        </div>
      )}

      {result && !error && (
        <div className="load-ai-import-message load-ai-import-message--success">
          <FiCheckCircle />

          <div>
            <strong>
              Transport order
              analysed
            </strong>

            <span>
              The detected data
              has been added to
              the form below.
            </span>
          </div>
        </div>
      )}

      {files.length > 0 && (
        <div className="load-ai-import-actions">
          <button
            type="button"
            className="load-ai-import-analyze"
            disabled={analyzing}
            onClick={
              handleAnalyze
            }
          >
            <FiZap />

            {analyzing
              ? "Analysing transport order..."
              : files.length === 1
                ? "Analyse document"
                : `Analyse ${files.length} documents`}
          </button>
        </div>
      )}
    </section>
  );
};

export default LoadDocumentUploader;